/**
 * POR PAGAR — «¿Qué tengo que pagar y cuándo?»
 *
 * Antes esto eran TRES destinos del menú principal (Tarjetas, Deudas,
 * Recurrentes), y para responder una sola pregunta había que visitar los tres
 * y sumar a mano. Peor: elegir el correcto exigía saber en qué colección se
 * había guardado la cosa.
 *
 * Aquí es una lista. El tipo aparece como una etiqueta pequeña para
 * desambiguar, no como la estructura de la pantalla.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { track } from "@/lib/productTelemetry";
import { money, whenShort, daysUntil, humanErrorLine } from "@/lib/human";
import { toast } from "sonner";
import { CreditCard, Landmark, Repeat, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND = {
  card: { icon: CreditCard, word: "Tarjeta" },
  debt: { icon: Landmark, word: "Préstamo" },
  recurring: { icon: Repeat, word: "Pago frecuente" },
};

export default function DuePage() {
  useEffect(() => { track("obligations_viewed", { screen: "obligations" }); }, []);
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/obligations", { params: { days: 90 } });
      setItems(data.items || []);
    } catch (e) { toast.error(humanErrorLine(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("td:data-changed", load);
    return () => window.removeEventListener("td:data-changed", load);
  }, [load]);

  // Tres grupos, en el orden en que a una persona le importan.
  const groups = [
    { key: "overdue", title: "Vencido", tone: "text-rose-300", pick: (i) => i.overdue },
    { key: "week", title: "Esta semana", tone: "text-amber-300", pick: (i) => !i.overdue && daysUntil(i.due_date) <= 7 },
    { key: "later", title: "Después", tone: "text-slate-400", pick: (i) => !i.overdue && daysUntil(i.due_date) > 7 },
  ];

  const total = (items || []).reduce((s, i) => s + Number(i.amount || 0), 0);
  const currency = items?.[0]?.currency || "DOP";

  return (
    <div className="space-y-6 pb-4" data-testid="due-page">
      <header>
        <h1 className="text-2xl font-bold text-white">Por pagar</h1>
        {items?.length > 0 && (
          <p className="text-[14px] text-slate-400 mt-1" data-testid="due-total">
            {items.length} {items.length === 1 ? "cosa" : "cosas"} por un total de{" "}
            <strong className="text-white font-semibold">{money(total, currency)}</strong>
          </p>
        )}
      </header>

      {items === null ? (
        <p className="text-[14px] text-slate-400">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="due-empty">
          <p className="text-[16px] font-semibold text-white">Todavía no tienes nada por pagar.</p>
          <p className="text-[14px] text-slate-400 mt-2 leading-relaxed max-w-sm mx-auto">
            Cuando agregues una tarjeta, un préstamo o un pago que se repite cada mes,
            lo verás aquí con su fecha.
          </p>
          <button onClick={() => nav("/mi-dinero")} data-testid="due-empty-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
            <Plus className="w-4 h-4" aria-hidden="true" /> Agregar algo
          </button>
        </div>
      ) : (
        <>
          {groups.map(g => {
            const rows = items.filter(g.pick);
            if (!rows.length) return null;
            return (
              <section key={g.key} data-testid={`due-group-${g.key}`}>
                <h2 className={`text-[13px] uppercase tracking-[0.14em] font-bold mb-2.5 ${g.tone}`}>
                  {g.title}
                </h2>
                <ul className="space-y-2">
                  {rows.map(i => <DueRow key={`${i.kind}-${i.id}`} item={i} onPay={() => window.dispatchEvent(new CustomEvent("td:quick", { detail: "pay" }))} />)}
                </ul>
              </section>
            );
          })}
          <button onClick={() => window.dispatchEvent(new CustomEvent("td:quick", { detail: "pay" }))} data-testid="due-pay-cta"
            className="w-full min-h-13 py-3.5 rounded-2xl bg-white text-[#15141b] font-bold">
            Pagar algo
          </button>
        </>
      )}

    </div>
  );
}

function DueRow({ item, onPay }) {
  const meta = KIND[item.kind] || KIND.card;
  const Icon = meta.icon;
  return (
    <li className={`flex items-center gap-3 p-4 rounded-2xl border min-h-[72px] ${
      item.overdue ? "bg-rose-500/10 border-rose-500/25" : "bg-white/[0.04] border-white/10"}`}
      data-testid={`due-item-${item.kind}-${item.id}`}>
      <span className="w-10 h-10 rounded-xl bg-white/[0.07] flex items-center justify-center shrink-0">
        <Icon className="w-[18px] h-[18px] text-slate-300" aria-hidden="true" />
      </span>
      {/* El nombre manda y puede ocupar dos líneas: «Préstamo del carro» cortado
          a «Préstamo …» obliga a adivinar cuál de los dos préstamos es. El tipo
          baja a la segunda línea, donde desambigua sin robar ancho. */}
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-semibold text-white leading-tight">{item.name}</span>
        <span className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-white/15 rounded px-1.5 py-0.5 shrink-0">
            {meta.word}
          </span>
          <span className={`text-[13px] truncate ${item.overdue ? "text-rose-200 font-semibold" : "text-slate-400"}`}>
            {whenShort(item.due_date)}
          </span>
        </span>
      </span>
      <span className="flex flex-col items-end gap-1.5 shrink-0">
        <span className="text-[16px] font-bold text-white tabular-nums">
          {money(item.amount, item.currency)}
        </span>
        <button onClick={onPay} data-testid={`due-pay-${item.kind}-${item.id}`}
          className="px-4 min-h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[13px] font-semibold">
          Pagar
        </button>
      </span>
    </li>
  );
}
